import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import { styled } from '@mui/material/styles';

const AttachmentContainer = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  padding: '8px 0',
  alignItems: 'center',
});

const FileIconButton = styled(IconButton)(({ theme }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '8px',
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

interface AttachmentIconsProps {
  files: AttachmentFile[];
  maxVisible?: number;
}

const getFileIcon = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  
  switch (extension) {
    case 'pdf':
      return <PictureAsPdfIcon color="error" />;
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'svg':
      return <ImageIcon color="primary" />;
    case 'doc':
    case 'docx':
      return <DescriptionIcon color="info" />;
    default:
      return <InsertDriveFileIcon />;
  }
};

export const AttachmentIcons = ({ files, maxVisible = 4 }: AttachmentIconsProps) => {
  if (!files || files.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        -
      </Typography>
    );
  }

  const visibleFiles = files.slice(0, maxVisible);
  const remainingCount = files.length - maxVisible;

  return (
    <AttachmentContainer>
      {visibleFiles.map((file) => (
        <Tooltip key={file.id} title={file.name} arrow>
          <FileIconButton
            size="small"
            onClick={() => window.open(file.url, '_blank')}
          >
            {getFileIcon(file.name)}
          </FileIconButton>
        </Tooltip>
      ))}
      {remainingCount > 0 && (
        <Tooltip title={`${remainingCount} more file${remainingCount > 1 ? 's' : ''}`} arrow>
          <Box
            sx={{
              width: '48px',
              height: '48px',
              borderRadius: '8px',
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'action.hover',
              cursor: 'default',
            }}
          >
            <Typography variant="caption" fontWeight="bold">
              +{remainingCount}
            </Typography>
          </Box>
        </Tooltip>
      )}
    </AttachmentContainer>
  );
};
